import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { MapPin, TrendingUp, Shield, Clock, Phone, Mail, Building2, ChevronRight, Star, ChevronDown, ClipboardList, CheckCircle2, AlertTriangle, Users, Award, Globe, Plane, Train, Trees, Palette, Wheat, Factory, ShoppingBag, Mountain } from 'lucide-react'
import { Button } from '@/components/ui'
import { CITY_NAME } from '@/config/supabase'

// Hero: vista panoramica Cagliari dal Bastione di Saint Remy al tramonto
const HERO_IMAGE = 'https://images.unsplash.com/photo-1570299630578-d53714c0aa9a?w=1920&q=80&auto=format&fit=crop'

// Sezione 3 - Tetti di Cagliari (mercato immobiliare locale)
const MARKET_IMAGE = 'https://images.unsplash.com/photo-1593075356257-252c29b02ce8?w=1200&q=80&auto=format&fit=crop'

// Sezione 6 - Bastione di Saint Remy, Cagliari (chi siamo / agenzia locale)
const AGENCY_IMAGE = 'https://images.unsplash.com/photo-1591792381642-a088238757f7?w=1200&q=80&auto=format&fit=crop'

// Hook scroll-triggered animations
function useInView<T extends HTMLElement>(threshold = 0.15): [React.RefObject<T>, boolean] {
  const ref = useRef<T>(null)
  const [inView, setInView] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true)
          obs.disconnect()
        }
      },
      { threshold, rootMargin: '0px 0px -50px 0px' }
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [threshold])
  return [ref, inView]
}

// Dati completi dei 9 comuni con foto Unsplash, prezzi e descrizioni
const comuni = [
  {
    name: 'Assemini',
    provincia: 'CA',
    abitanti: '~28.000',
    priceRange: '€800-1.300/mq',
    description: 'Importante polo industriale (Macchiareddu), buoni servizi e collegamenti ferroviari con Cagliari.',
    highlights: ['Polo industriale Macchiareddu', 'Stazione FS', 'Ben servita'],
    icon: Factory,
    color: 'from-blue-500 to-cyan-500',
    image: 'https://images.unsplash.com/photo-1472207241423-9e30d66d4b0f?w=800&q=80&auto=format&fit=crop',
  },
  {
    name: 'Sestu',
    provincia: 'CA',
    abitanti: '~21.000',
    priceRange: '€950-1.550/mq',
    description: 'Comune in forte crescita, importante polo commerciale (Le Vele, La Corte del Sole), vicinissimo a Cagliari.',
    highlights: ['Centri commerciali', 'Crescita demografica', 'Servizi moderni'],
    icon: ShoppingBag,
    color: 'from-emerald-500 to-teal-500',
    image: 'https://images.unsplash.com/photo-1647857615661-f8925b95f07a?w=800&q=80&auto=format&fit=crop',
  },
  {
    name: 'Elmas',
    provincia: 'CA',
    abitanti: '~9.500',
    priceRange: '€950-1.500/mq',
    description: 'Sede dell\'aeroporto di Cagliari-Elmas. Collegamenti rapidi al centro e al porto di Cagliari.',
    highlights: ['Aeroporto Cagliari', 'Stazione ferroviaria', 'Hub trasporti'],
    icon: Plane,
    color: 'from-sky-500 to-indigo-500',
    image: 'https://images.unsplash.com/photo-1565863406966-c8abb1bd8bd1?w=800&q=80&auto=format&fit=crop',
  },
  {
    name: 'Uta',
    provincia: 'CA',
    abitanti: '~8.000',
    priceRange: '€680-1.000/mq',
    description: 'Hinterland sud-ovest, contesto rurale-residenziale. Vicina al parco naturale di Gutturu Mannu.',
    highlights: ['Parco Gutturu Mannu', 'Ambiente naturale', 'Tranquillità'],
    icon: Trees,
    color: 'from-green-500 to-emerald-500',
    image: 'https://images.unsplash.com/photo-1743844914084-5145ceb48b58?w=800&q=80&auto=format&fit=crop',
  },
  {
    name: 'Decimomannu',
    provincia: 'SU',
    abitanti: '~7.800',
    priceRange: '€720-1.050/mq',
    description: 'Snodo ferroviario importante, base aeronautica militare. Prezzi accessibili, ottimi collegamenti.',
    highlights: ['Snodo ferroviario', 'Base militare', 'Collegamenti rapidi'],
    icon: Train,
    color: 'from-violet-500 to-purple-500',
    image: 'https://images.unsplash.com/photo-1552026303-99c2b42da468?w=800&q=80&auto=format&fit=crop',
  },
  {
    name: 'Decimoputzu',
    provincia: 'SU',
    abitanti: '~3.000',
    priceRange: '€640-950/mq',
    description: 'Piccolo comune del Campidano, contesto rurale tranquillo. Prezzi tra i più bassi dell\'area.',
    highlights: ['Campidano', 'Prezzi accessibili', 'Vita rurale'],
    icon: Wheat,
    color: 'from-amber-500 to-yellow-500',
    image: 'https://images.unsplash.com/photo-1561396208-7e587c04c3f5?w=800&q=80&auto=format&fit=crop',
  },
  {
    name: 'San Sperate',
    provincia: 'SU',
    abitanti: '~8.000',
    priceRange: '€700-1.030/mq',
    description: 'Famoso "paese museo a cielo aperto" per i murales dell\'artista Pinuccio Sciola. Forte identità culturale.',
    highlights: ['Paese dei murales', 'Pinuccio Sciola', 'Cultura e arte'],
    icon: Palette,
    color: 'from-pink-500 to-rose-500',
    image: 'https://images.unsplash.com/photo-1755424278939-2daa797de986?w=800&q=80&auto=format&fit=crop',
  },
  {
    name: 'Villaspeciosa',
    provincia: 'SU',
    abitanti: '~2.500',
    priceRange: '€620-900/mq',
    description: 'Piccolo borgo del Campidano. Contesto rilassato, spazi ampi, prezzi tra i più contenuti.',
    highlights: ['Borgo del Campidano', 'Spazi ampi', 'Comunità coesa'],
    icon: Mountain,
    color: 'from-stone-500 to-zinc-500',
    image: 'https://images.unsplash.com/photo-1558508006-b47365cf0e61?w=800&q=80&auto=format&fit=crop',
  },
  {
    name: 'Villasor',
    provincia: 'SU',
    abitanti: '~7.000',
    priceRange: '€600-880/mq',
    description: 'Cuore del Campidano agricolo. Presenza del castello Siviller. Comunità coesa, tradizione contadina.',
    highlights: ['Castello Siviller', 'Campidano agricolo', 'Tradizione'],
    icon: Building2,
    color: 'from-orange-500 to-red-500',
    image: 'https://images.unsplash.com/photo-1591103000599-50f5b4ec7d3d?w=800&q=80&auto=format&fit=crop',
  },
]

const features = [
  {
    icon: MapPin,
    title: 'Dati Reali del Compravenduto',
    description: 'Quotazioni immobiliari basate sulle compravendite reali degli ultimi 12 mesi per tutte le zone di Cagliari',
    color: 'from-blue-500 to-cyan-500',
  },
  {
    icon: TrendingUp,
    title: 'Analisi Dettagliata al Mq',
    description: 'Coefficienti specifici per stato, piano, esposizione, ascensore e ogni caratteristica del tuo immobile nell\'hinterland di Cagliari',
    color: 'from-emerald-500 to-teal-500',
  },
  {
    icon: Clock,
    title: 'Risultato Immediato Online',
    description: 'Scopri il valore della tua casa in 2 minuti senza appuntamento e senza impegno',
    color: 'from-amber-500 to-orange-500',
  },
  {
    icon: Shield,
    title: 'Gratuito e Senza Impegno',
    description: 'Valutazione completamente gratuita. I tuoi dati sono protetti e trattati con riservatezza',
    color: 'from-violet-500 to-purple-500',
  },
]

const stats = [
  { value: '500+', label: 'Valutazioni effettuate' },
  { value: '98%', label: 'Clienti soddisfatti' },
  { value: '15+', label: 'Anni di esperienza' },
]

const steps = [
  {
    icon: MapPin,
    number: '1',
    title: 'Seleziona la zona',
    description: 'Scegli la zona di Cagliari dove si trova il tuo immobile',
  },
  {
    icon: ClipboardList,
    number: '2',
    title: 'Inserisci i dettagli',
    description: 'Metratura, piano, stato dell\'immobile, esposizione e altre caratteristiche',
  },
  {
    icon: TrendingUp,
    number: '3',
    title: 'Ricevi la valutazione',
    description: 'Ottieni subito la stima del valore con il prezzo al metro quadro della tua zona',
  },
]


const testimonials = [
  {
    text: 'Servizio di valutazione rapido e intuitivo. In pochi minuti ho avuto la stima del mio appartamento a Sestu, davvero semplice da usare.',
    name: 'Marco Deriu',
    date: '2026',
    initials: 'MD',
    bgColor: 'bg-gray-400',
  },
  {
    text: 'Sito veloce e immediato. Ho ottenuto la valutazione del mio bilocale ad Assemini in 2 minuti, senza complicazioni.',
    name: 'Anna Sanna',
    date: '2026',
    initials: 'AS',
    bgColor: 'bg-orange-500',
  },
  {
    text: 'Strumento intuitivo e ben fatto. Mi ha aiutato a capire il valore reale della mia casa a Elmas in modo semplice e chiaro.',
    name: 'Giuseppe Ferrara',
    date: '2026',
    initials: 'GF',
    bgColor: 'bg-purple-500',
  },
  {
    text: 'Valutazione veloce e affidabile. La stima del mio immobile a Uta è risultata perfettamente in linea con il mercato.',
    name: 'Lucia Piras',
    date: '2026',
    initials: 'LP',
    bgColor: 'bg-pink-500',
  },
  {
    text: 'Servizio rapido e preciso. Per la valutazione della nostra villa a Villasor ci ha dato un riferimento chiaro e immediato.',
    name: 'Roberto Cossu',
    date: '2026',
    initials: 'RC',
    bgColor: 'bg-blue-500',
  },
  {
    text: 'Esperienza eccellente. Semplice, veloce e gratuito: ho ricevuto la valutazione del mio appartamento a Decimomannu in pochi minuti.',
    name: 'Francesca Mura',
    date: '2026',
    initials: 'FM',
    bgColor: 'bg-teal-500',
  },
]

const faqs = [
  {
    question: 'Come funziona la valutazione immobiliare online nell\'hinterland di Cagliari?',
    answer: 'Inserisci i dati del tuo immobile (zona, metratura, caratteristiche) nel nostro form e ricevi in 2 minuti una stima basata sui dati reali del compravenduto degli ultimi 12 mesi nell\'hinterland di Cagliari. Il nostro algoritmo applica coefficienti specifici per ogni caratteristica della tua proprietà: stato di conservazione, piano, esposizione, ascensore, terrazzo e box auto. Il risultato è una valutazione personalizzata con il prezzo al metro quadro aggiornato per la tua zona specifica di Cagliari.',
  },
  {
    question: 'La valutazione della casa nell\'hinterland di Cagliari è gratuita?',
    answer: 'Sì, la valutazione online è completamente gratuita e senza impegno. Non è richiesta alcuna registrazione né carta di credito. Riceverai una stima dettagliata del valore del tuo immobile basata sui dati reali delle compravendite immobiliari nell\'hinterland di Cagliari degli ultimi 12 mesi, con un report che include il range di prezzo, il prezzo al metro quadro e i fattori che influenzano la valutazione.',
  },
  {
    question: 'Quali dati vengono usati per la stima immobiliare nell\'hinterland di Cagliari?',
    answer: 'Utilizziamo i dati reali del compravenduto degli ultimi 12 mesi nell\'hinterland di Cagliari, ovvero i prezzi effettivi a cui gli immobili sono stati venduti nella tua zona. Questi dati vengono combinati con coefficienti specifici per stato dell\'immobile, piano, esposizione, presenza di ascensore, terrazzo, giardino e altre caratteristiche. Questo approccio basato su transazioni reali garantisce una stima più accurata rispetto a semplici medie di mercato.',
  },
  {
    question: 'Quanto è precisa la valutazione rispetto al valore reale?',
    answer: 'La nostra valutazione fornisce una stima indicativa molto attendibile perché basata su compravendite reali nella tua zona di Cagliari. Il margine di precisione dipende dalla quantità di transazioni recenti nella microzona specifica. Per immobili con caratteristiche particolari o per una perizia estimativa ufficiale da utilizzare in ambito legale o bancario, consigliamo di contattare la nostra agenzia di riferimento per un sopralluogo professionale gratuito.',
  },
  {
    question: 'Quali comuni dell\'hinterland di Cagliari sono coperti dalla valutazione?',
    answer: 'Copriamo i 9 comuni dell\'hinterland sud di Cagliari: Assemini, Sestu, Elmas e Uta in provincia di Cagliari; Decimomannu, Decimoputzu, San Sperate, Villaspeciosa e Villasor in provincia del Sud Sardegna. Ogni comune ha quotazioni specifiche basate sul compravenduto reale, dalle zone centrali alle periferiche. Il nostro database copre tutte le microzone con dati aggiornati sulle transazioni immobiliari effettive.',
  },
  {
    question: 'Quanto vale una casa al metro quadro nell\'hinterland di Cagliari?',
    answer: 'Il prezzo al metro quadro nell\'hinterland di Cagliari varia per comune: Sestu ed Elmas (i più vicini al capoluogo) si attestano tra 1.000 e 1.500 €/mq per immobili ristrutturati, Assemini tra 800 e 1.300 €/mq, mentre Decimomannu, Uta, San Sperate, Villasor, Decimoputzu e Villaspeciosa partono da circa 600-900 €/mq. La nostra valutazione calcola il prezzo specifico per la tua zona e le caratteristiche del tuo immobile, basandosi sulle compravendite effettive degli ultimi mesi.',
  },
  {
    question: 'Serve una valutazione per richiedere un mutuo nell\'hinterland di Cagliari?',
    answer: 'Per la richiesta di un mutuo ipotecario, la banca invia un proprio perito per effettuare una perizia ufficiale dell\'immobile. Tuttavia, la nostra valutazione online gratuita è un ottimo strumento per avere un primo riferimento sul valore del tuo immobile prima di rivolgerti alla banca. Questo ti permette di stimare in anticipo l\'importo finanziabile e di confrontare le proposte di diversi istituti di credito. Tieni presente che la perizia bancaria tende ad essere più conservativa rispetto al prezzo di mercato, poiché la banca valuta l\'immobile come garanzia del credito.',
  },
  {
    question: 'Conviene ristrutturare casa prima di vendere nell\'hinterland di Cagliari?',
    answer: 'Dipende dal tipo di intervento e dalla zona. A Cagliari, un immobile ristrutturato può valere dal 10% al 20% in più rispetto a uno da ristrutturare nella stessa zona. Interventi mirati come il rifacimento del bagno, la tinteggiatura e l\'ammodernamento degli impianti possono aumentare significativamente l\'attrattività dell\'immobile e ridurre i tempi di vendita. Tuttavia, ristrutturazioni importanti potrebbero non essere convenienti se il costo supera il margine di rivalutazione. Il nostro consiglio è di effettuare prima una valutazione dell\'immobile nello stato attuale e poi valutare con la nostra agenzia di riferimento se e quali interventi conviene fare.',
  },
  {
    question: 'Quali tasse si pagano sulla compravendita immobiliare nell\'hinterland di Cagliari?',
    answer: 'Le tasse sulla compravendita variano in base al tipo di acquisto. Per la prima casa acquistata da privato: imposta di registro al 2% del valore catastale, più imposte ipotecaria e catastale fisse (50€ + 50€). Per la seconda casa da privato: imposta di registro al 9%. Se si acquista da costruttore (impresa): IVA al 4% per la prima casa o al 10% per la seconda casa, più imposte fisse. A queste si aggiunge l\'IMU per le seconde case, calcolata sulla rendita catastale rivalutata con l\'aliquota stabilita dal Comune di Cagliari. Per gli affitti brevi, è possibile optare per la cedolare secca al 21% (o 26% dal 2024 per la seconda unità in poi).',
  },
  {
    question: 'Quanto costa un\'agenzia immobiliare nell\'hinterland di Cagliari?',
    answer: 'Le provvigioni di un\'agenzia immobiliare nell\'hinterland di Cagliari variano generalmente dal 2% al 4% del prezzo di vendita, sia per il venditore che per l\'acquirente. Il servizio include la valutazione dell\'immobile, la promozione sui principali portali, la gestione delle visite, la negoziazione del prezzo, l\'assistenza burocratica fino al rogito notarile e anche la valutazione online gratuita basata sui dati reali del compravenduto. Affidarsi a un professionista locale con esperienza nel mercato di Cagliari può fare la differenza in termini di prezzo ottenuto e tempi di vendita, spesso ripagando ampiamente il costo della provvigione.',
  },
]

const seoSections = [
  {
    title: 'Valutazione Immobiliare nella Zona Metropolitana di Cagliari',
    content: (
      <div className="space-y-5">
        <p>
          Stai pensando di <strong>vendere casa nella zona metropolitana di Cagliari</strong> o semplicemente vuoi sapere <strong>quanto vale il tuo immobile</strong>? Conoscere il valore reale della propria abitazione è il primo passo fondamentale per qualsiasi decisione immobiliare. Che si tratti di una compravendita, di una successione, di una separazione o della richiesta di un mutuo, avere una <strong>stima immobiliare accurata nella zona metropolitana di Cagliari</strong> ti permette di muoverti con sicurezza nel mercato.
        </p>
        <p>
          Il nostro strumento di <strong>valutazione casa nella zona metropolitana di Cagliari</strong> ti offre una stima professionale e gratuita in soli 2 minuti. A differenza di altre soluzioni generiche, la nostra valutazione si basa sui <strong>dati reali delle compravendite effettuate negli ultimi 12 mesi</strong> nella tua zona specifica. Questo significa che il prezzo al metro quadro che ottieni riflette l'andamento reale del mercato immobiliare locale, non semplici medie statistiche nazionali.
        </p>
        <p>
          Ogni immobile è unico: per questo il nostro algoritmo tiene conto di tutti i fattori che influenzano il <strong>valore di una casa nella zona metropolitana di Cagliari</strong> — dalla posizione esatta alla metratura, dallo stato di conservazione al piano, dalla presenza dell'ascensore all'esposizione, fino al terrazzo, al giardino e al box auto. Il risultato è una valutazione personalizzata e dettagliata che ti mostra il range di prezzo del tuo immobile con il <strong>prezzo al metro quadro</strong> aggiornato per la tua zona.
        </p>
      </div>
    ),
  },
  {
    title: 'Prezzi e Tendenze del Mercato',
    content: (
      <div className="space-y-5">
        <p>
          Il <strong>mercato immobiliare dell'hinterland di Cagliari</strong> è uno dei più dinamici della <strong>Sardegna meridionale</strong>. Grazie alla vicinanza al capoluogo, all'aeroporto di Elmas e alle principali vie di comunicazione, i 9 comuni coperti attirano acquirenti locali, lavoratori pendolari e famiglie alla ricerca di soluzioni residenziali più accessibili rispetto a Cagliari città. Il <strong>prezzo delle case</strong> varia in base al comune e alle caratteristiche dell'immobile.
        </p>
        <p>
          Nei comuni più vicini al capoluogo come <strong>Sestu</strong> ed <strong>Elmas</strong>, i prezzi al metro quadro raggiungono i <strong>1.000-1.500 €/mq</strong> per immobili ristrutturati. <strong>Assemini</strong> e <strong>Uta</strong> si attestano tra <strong>800 e 1.300 €/mq</strong>. I comuni del Sud Sardegna come <strong>Decimomannu</strong>, <strong>San Sperate</strong>, <strong>Villasor</strong>, <strong>Decimoputzu</strong> e <strong>Villaspeciosa</strong> presentano valori tra <strong>600 e 950 €/mq</strong>, con il vantaggio di spazi più ampi e maggiore tranquillità.
        </p>
        <p>
          I fattori che incidono maggiormente sul <strong>valore di un immobile nell'hinterland cagliaritano</strong> sono l'esposizione e la luminosità (che possono aumentare il valore fino al 10-15%), lo stato di conservazione (un appartamento da ristrutturare può valere il 10-15% in meno), il piano dell'immobile e la presenza di ascensore, terrazzo, giardino o box auto. Anche la vicinanza all'aeroporto, al centro di Cagliari e alle principali infrastrutture (SS 130, SS 131) influisce significativamente sulla valutazione finale.
        </p>
        <p>
          Rispetto a <strong>Cagliari città</strong>, l'hinterland offre un rapporto qualità-prezzo molto competitivo con prezzi generalmente più accessibili. La zona è caratterizzata da una forte presenza di servizi, scuole e collegamenti rapidi con il capoluogo. <strong>Comprare casa nell'hinterland di Cagliari</strong> rappresenta un investimento solido, sia per chi cerca una residenza principale che per chi desidera maggiore qualità della vita restando vicino al centro di Cagliari.
        </p>
      </div>
    ),
  },
  {
    title: 'Come Valutare Correttamente una Casa',
    content: (
      <div className="space-y-5">
        <p>
          <strong>Valutare una casa nella zona metropolitana di Cagliari</strong> richiede di considerare molteplici fattori. Il primo è la <strong>posizione</strong>: un appartamento in centro avrà un valore molto diverso da uno in zona periferica. Il secondo fattore è la <strong>metratura commerciale</strong>, che include non solo la superficie calpestabile ma anche balconi, terrazzi e pertinenze calcolati con le opportune percentuali. Lo <strong>stato di conservazione</strong> è determinante: un immobile ristrutturato di recente può valere fino al 20% in più rispetto a uno da ristrutturare nella stessa zona.
        </p>
        <p>
          La nostra <strong>valutazione immobiliare online</strong> si distingue perché utilizza i <strong>dati reali delle compravendite degli ultimi 12 mesi</strong> nella zona metropolitana di Cagliari, non stime teoriche. Questo approccio garantisce che il prezzo al metro quadro rifletta il valore effettivo a cui gli immobili vengono realmente comprati e venduti nella tua zona. È utile fare una valutazione quando stai pensando di <strong>vendere casa</strong>, vuoi <strong>comprare un immobile</strong> e verificare se il prezzo richiesto è congruo, devi gestire una <strong>successione</strong> o una <strong>separazione patrimoniale</strong>, oppure hai bisogno di un riferimento per la richiesta di un <strong>mutuo</strong>.
        </p>
        <p>
          Per una stima ancora più precisa, dopo aver ottenuto la valutazione online puoi contattare la nostra <strong>agenzia di riferimento</strong> per un sopralluogo professionale gratuito. I nostri consulenti immobiliari conoscono ogni angolo della zona metropolitana di Cagliari e possono fornirti una <strong>perizia estimativa dettagliata</strong> che tiene conto di ogni singola caratteristica del tuo immobile.
        </p>
      </div>
    ),
  },
  {
    title: 'Vendere Casa nella Zona Metropolitana di Cagliari: Guida Completa',
    content: (
      <div className="space-y-5">
        <p>
          <strong>Vendere casa a {CITY_NAME}</strong> è una decisione importante che richiede preparazione e strategia. Il primo passo fondamentale è conoscere il <strong>valore reale del tuo immobile</strong>: fissare un prezzo troppo alto significa allungare i tempi di vendita e scoraggiare i potenziali acquirenti, mentre un prezzo troppo basso rischia di farti perdere migliaia di euro. La nostra <strong>valutazione gratuita online</strong> ti offre un punto di partenza affidabile basato sulle compravendite effettive nella tua zona di {CITY_NAME}.
        </p>
        <p>
          Prima di mettere in <strong>vendita il tuo appartamento a {CITY_NAME}</strong>, è importante raccogliere tutta la documentazione necessaria: visura catastale aggiornata, planimetria conforme allo stato di fatto, attestato di prestazione energetica (APE), certificato di agibilità e, se presente, la documentazione condominiale con le ultime delibere assembleari e il regolamento. Avere tutto in ordine fin dall'inizio accelera notevolmente i tempi della <strong>compravendita immobiliare</strong>.
        </p>
        <p>
          Il mercato immobiliare dell'hinterland di Cagliari presenta caratteristiche interessanti: la domanda è sostenuta da acquirenti locali, famiglie che cercano spazi più ampi e professionisti che lavorano a Cagliari città ma preferiscono vivere fuori dal traffico. La vicinanza all'<strong>aeroporto di Elmas</strong>, al <strong>porto di Cagliari</strong> e alle principali arterie (SS 130, SS 131) rende quest'area un punto strategico. Affidarsi a un'<strong>agenzia immobiliare locale</strong> significa avere accesso a una rete di contatti qualificati e a una conoscenza approfondita del territorio che può fare la differenza tra una vendita rapida al giusto prezzo e mesi di attesa.
        </p>
        <p>
          I tempi medi di <strong>vendita di un immobile a {CITY_NAME}</strong> variano in base alla zona e alla tipologia: gli appartamenti nelle zone centrali e ben servite si vendono mediamente più velocemente rispetto alle zone periferiche, ma ogni caso è unico. Un immobile ben presentato, con un prezzo allineato al mercato e una promozione efficace, può trovare un acquirente in poche settimane.
        </p>
      </div>
    ),
  },
  {
    title: 'Comprare Casa nella Zona Metropolitana di Cagliari: Cosa Sapere',
    content: (
      <div className="space-y-5">
        <p>
          <strong>Comprare casa nell'hinterland di Cagliari</strong> è un'opportunità interessante per chi cerca soluzioni residenziali più accessibili rispetto al capoluogo. L'area offre un'ottima qualità della vita, una posizione strategica a pochi minuti da Cagliari città, dall'aeroporto di Elmas e dalle spiagge del Poetto. Comuni come <strong>San Sperate</strong> (famoso per i murales), <strong>Sestu</strong> (importante polo commerciale) e <strong>Villasor</strong> (Campidano) offrono ognuno un'identità territoriale forte unita a infrastrutture moderne.
        </p>
        <p>
          Prima di <strong>acquistare un immobile a {CITY_NAME}</strong>, è essenziale verificare alcuni aspetti fondamentali: la conformità urbanistica e catastale dell'immobile, lo stato di conservazione reale (un sopralluogo approfondito può rivelare problemi nascosti come umidità, infiltrazioni o impianti obsoleti), le spese condominiali ordinarie e straordinarie, e la classe energetica. Per gli immobili in condominio, è importante esaminare i verbali delle ultime assemblee per scoprire eventuali lavori straordinari deliberati o in programma.
        </p>
        <p>
          Il <strong>prezzo degli appartamenti nell'hinterland di Cagliari</strong> varia in base al comune: si va dai <strong>600 €/mq</strong> nelle zone più periferiche di Villasor e Decimoputzu, fino a circa <strong>1.500 €/mq</strong> per immobili ristrutturati a Sestu ed Elmas (più vicini al capoluogo). Comuni come <strong>Assemini</strong>, <strong>Uta</strong> e <strong>Decimomannu</strong> offrono un buon compromesso tra accessibilità dei prezzi, servizi e qualità della vita, con valori compresi tra <strong>700 e 1.100 €/mq</strong>.
        </p>
        <p>
          Per chi <strong>compra la prima casa a {CITY_NAME}</strong>, sono disponibili agevolazioni fiscali significative: imposta di registro ridotta al 2% (invece del 9%) o IVA al 4% per acquisti da costruttore, oltre a possibili agevolazioni per giovani under 36. Utilizzare la nostra <strong>valutazione online</strong> prima di un acquisto ti permette di verificare se il prezzo richiesto dal venditore è allineato ai valori di mercato reali della zona.
        </p>
      </div>
    ),
  },
  {
    title: 'Investimento Immobiliare nella Zona Metropolitana di Cagliari: Opportunità',
    content: (
      <div className="space-y-5">
        <p>
          L'<strong>hinterland di Cagliari</strong> rappresenta un'opportunità interessante per l'<strong>investimento immobiliare</strong> in Sardegna. La vicinanza al capoluogo, all'<strong>Università degli Studi di Cagliari</strong>, all'aeroporto e ai principali poli industriali (Macchiareddu, Sarroch) garantisce una domanda costante di <strong>affitti</strong> da parte di studenti, lavoratori pendolari e famiglie. Le infrastrutture in crescita rendono l'area sempre più attrattiva.
        </p>
        <p>
          Dal punto di vista dell'<strong>investimento</strong>, un appartamento nell'hinterland può generare reddito attraverso gli <strong>affitti a lavoratori e studenti</strong>. I comuni più richiesti sono <strong>Sestu</strong>, <strong>Elmas</strong> e <strong>Assemini</strong>, vicini al capoluogo e ben collegati. Un appartamento bilocale ristrutturato in queste zone può generare un rendimento lordo annuo del <strong>5-7%</strong> attraverso locazioni a lungo termine.
        </p>
        <p>
          Per quanto riguarda la <strong>tassazione</strong>, è importante considerare l'<strong>IMU</strong> (Imposta Municipale Unica), che per gli immobili a reddito si calcola con un'aliquota stabilita dal Comune sulla rendita catastale rivalutata. A questo si aggiunge la <strong>TARI</strong> (tassa rifiuti) e, in caso di locazione, la possibilità di optare per la <strong>cedolare secca</strong> al 21% (o 26% per affitti brevi dal 2024 per la seconda unità in poi). L'imposta di registro per l'acquisto è del <strong>9%</strong> del valore catastale, oppure IVA al 10% se si acquista da costruttore.
        </p>
        <p>
          Rispetto a <strong>Cagliari città</strong>, l'hinterland offre prezzi sensibilmente più accessibili con rendimenti da affitto competitivi grazie al rapporto favorevole tra prezzo di acquisto e canone di locazione. La crescita continua delle infrastrutture (espansione dell'aeroporto, miglioramenti delle SS 130/131) aggiunge potenziale di rivalutazione nel medio-lungo termine, rendendo l'area particolarmente interessante per chi cerca un investimento solido.
        </p>
      </div>
    ),
  },
  {
    title: 'Perizia Immobiliare vs Valutazione Online',
    content: (
      <div className="space-y-5">
        <p>
          Quando si parla di <strong>valutazione immobiliare a {CITY_NAME}</strong>, è importante distinguere tra la <strong>valutazione online gratuita</strong> e la <strong>perizia immobiliare professionale</strong>. La nostra valutazione online utilizza i dati reali del compravenduto degli ultimi 12 mesi e applica coefficienti specifici per le caratteristiche del tuo immobile, fornendo una <strong>stima indicativa affidabile</strong> del valore di mercato. È uno strumento ideale per avere un primo riferimento rapido e gratuito.
        </p>
        <p>
          La <strong>perizia immobiliare</strong>, invece, è un documento redatto da un professionista abilitato (perito o tecnico iscritto all'albo) che effettua un sopralluogo fisico dell'immobile, verifica la conformità urbanistica e catastale, analizza lo stato manutentivo e produce una <strong>relazione estimativa dettagliata</strong>. Questo tipo di valutazione ha valore legale e viene richiesta in specifiche circostanze: per l'erogazione di un <strong>mutuo ipotecario</strong> (la banca invia il proprio perito), in caso di <strong>successione ereditaria</strong>, durante una <strong>separazione o divorzio</strong> per la divisione dei beni, e in <strong>contenziosi legali</strong> relativi a immobili.
        </p>
        <p>
          Il nostro consiglio è di utilizzare la <strong>valutazione online gratuita</strong> come primo step per orientarti sul valore del tuo immobile nella zona metropolitana di Cagliari. Se poi hai bisogno di una <strong>perizia ufficiale</strong>, la nostra <strong>agenzia di riferimento</strong> può metterti in contatto con professionisti qualificati del territorio. In molti casi, i nostri clienti utilizzano la valutazione online per decidere se procedere con la vendita e poi richiedono un sopralluogo gratuito ai nostri agenti per una stima ancora più precisa.
        </p>
        <p>
          È utile sapere che la <strong>stima di un perito bancario</strong> tende ad essere più conservativa rispetto al prezzo di mercato, poiché le banche valutano l'immobile in un'ottica di garanzia del credito. La nostra valutazione, basata sulle <strong>compravendite effettive</strong>, riflette invece il prezzo reale a cui gli immobili vengono venduti nella tua zona, offrendo un quadro più completo del <strong>mercato immobiliare locale di {CITY_NAME}</strong>.
        </p>
      </div>
    ),
  },
]

// ============================================
// SEZIONE 9: I 9 COMUNI con foto Unsplash
// ============================================
function ComuniSection() {
  const [ref, inView] = useInView<HTMLDivElement>()
  return (
    <section className="py-24 bg-gradient-to-b from-white via-gray-50 to-white relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -left-40 w-96 h-96 bg-primary-100/40 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-accent-300/30 rounded-full blur-3xl" />
      </div>

      <div ref={ref} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className={`text-center mb-16 ${inView ? 'animate-fade-in-up' : 'opacity-0'}`}>
          <span className="inline-block text-primary-600 font-semibold text-sm uppercase tracking-wider bg-primary-50 px-4 py-1.5 rounded-full">
            Specializzati nell'hinterland sud
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mt-5 leading-tight">
            I 9 Comuni dove Operiamo
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-primary-700 mt-2">
              con Quotazioni Reali
            </span>
          </h2>
          <p className="mt-6 text-gray-600 max-w-3xl mx-auto text-lg leading-relaxed">
            Conosciamo a fondo ogni singolo comune dell'hinterland sud di {CITY_NAME}.
            Ogni territorio ha caratteristiche uniche che influenzano il valore degli immobili.
            Le nostre quotazioni si basano sul <strong>compravenduto reale degli ultimi 12 mesi</strong>.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {comuni.map((comune, idx) => (
            <ComuneCard key={comune.name} comune={comune} delay={idx * 80} inView={inView} />
          ))}
        </div>

        <div className={`mt-16 text-center ${inView ? 'animate-fade-in-up-delay-3' : 'opacity-0'}`}>
          <Link to="/valutazione">
            <button className="bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white font-bold px-10 py-4 text-lg rounded-2xl shadow-xl hover:shadow-2xl transition-all transform hover:-translate-y-0.5 inline-flex items-center gap-2">
              Valuta la Tua Casa nel Tuo Comune
              <ChevronRight className="h-5 w-5" />
            </button>
          </Link>
        </div>
      </div>
    </section>
  )
}

function ComuneCard({ comune, delay, inView }: { comune: typeof comuni[number]; delay: number; inView: boolean }) {
  const Icon = comune.icon
  return (
    <div
      className={`group bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-500 hover:-translate-y-1 border border-gray-100 ${
        inView ? 'animate-fade-in-up' : 'opacity-0'
      }`}
      style={{ animationDelay: `${delay}ms`, animationFillMode: 'both' }}
    >
      <div className={`relative h-48 overflow-hidden bg-gradient-to-br ${comune.color}`}>
        <img
          src={comune.image}
          alt={`${comune.name} - Sardegna`}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          loading="lazy"
          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        <div className="absolute top-3 right-3 bg-white/95 backdrop-blur-sm rounded-full px-3 py-1 text-xs font-bold text-gray-700">
          {comune.provincia === 'CA' ? 'Provincia CA' : 'Sud Sardegna'}
        </div>
        <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between">
          <div>
            <h3 className="text-2xl font-bold text-white drop-shadow-lg">{comune.name}</h3>
            <p className="text-white/90 text-xs drop-shadow">{comune.abitanti} abitanti</p>
          </div>
          <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${comune.color} flex items-center justify-center shadow-lg ring-2 ring-white/40`}>
            <Icon className="h-6 w-6 text-white" />
          </div>
        </div>
      </div>

      <div className="p-5">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs uppercase tracking-wider text-gray-400 font-semibold">Quotazione media</span>
          <span className="text-lg font-bold text-primary-600">{comune.priceRange}</span>
        </div>
        <p className="text-sm text-gray-600 leading-relaxed mb-4">{comune.description}</p>
        <div className="flex flex-wrap gap-1.5 mb-4">
          {comune.highlights.map((h) => (
            <span key={h} className="text-xs bg-primary-50 text-primary-700 px-2.5 py-1 rounded-full font-medium">
              {h}
            </span>
          ))}
        </div>
        <Link
          to="/valutazione"
          className="inline-flex items-center gap-1 text-sm font-semibold text-primary-600 hover:text-primary-700 group/link"
        >
          Valuta a {comune.name}
          <ChevronRight className="h-4 w-4 transition-transform group-hover/link:translate-x-1" />
        </Link>
      </div>
    </div>
  )
}

// ============================================
// SEZIONE 9B: Perché scegliere noi
// ============================================
function PerchéSection() {
  const [ref, inView] = useInView<HTMLDivElement>()
  const reasons = [
    {
      icon: MapPin,
      title: 'Specialisti del Territorio',
      description: 'Solo 9 comuni, conosciuti uno per uno. Quotazioni reali per ogni microzona, non medie nazionali.',
      color: 'bg-blue-100 text-blue-600',
    },
    {
      icon: TrendingUp,
      title: 'Dati Aggiornati al 2026',
      description: 'Compravendite degli ultimi 12 mesi, non statistiche obsolete. Algoritmo aggiornato continuamente.',
      color: 'bg-emerald-100 text-emerald-600',
    },
    {
      icon: Clock,
      title: 'Valutazione in 2 Minuti',
      description: 'Niente appuntamenti, niente attese. Compili il form e ricevi la stima immediatamente.',
      color: 'bg-amber-100 text-amber-600',
    },
    {
      icon: Shield,
      title: '100% Gratuito',
      description: 'Nessun costo, nessun impegno, nessuna carta di credito. Privacy garantita.',
      color: 'bg-violet-100 text-violet-600',
    },
    {
      icon: Users,
      title: 'Consulenza Umana',
      description: 'Dopo la stima online, un nostro agente locale ti contatta per un eventuale sopralluogo gratuito.',
      color: 'bg-pink-100 text-pink-600',
    },
    {
      icon: Award,
      title: 'Esperienza Pluriennale',
      description: `Centinaia di compravendite gestite nell'hinterland di ${CITY_NAME}. Recensioni Google verificate.`,
      color: 'bg-orange-100 text-orange-600',
    },
  ]
  return (
    <section className="py-24 bg-gradient-to-br from-gray-900 via-primary-900 to-gray-900 text-white relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary-500/10 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-accent-400/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }} />
      </div>

      <div ref={ref} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className={`text-center mb-16 ${inView ? 'animate-fade-in-up' : 'opacity-0'}`}>
          <span className="inline-block text-accent-400 font-semibold text-sm uppercase tracking-wider">
            Perché sceglierci
          </span>
          <h2 className="text-3xl md:text-5xl font-bold mt-4 leading-tight">
            Il Modo Più Smart per Conoscere
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-accent-300 to-accent-500 mt-1">
              il Valore della Tua Casa
            </span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reasons.map((r, idx) => (
            <div
              key={r.title}
              className={`bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-7 hover:bg-white/10 hover:border-white/20 transition-all duration-500 group ${
                inView ? 'animate-fade-in-up' : 'opacity-0'
              }`}
              style={{ animationDelay: `${idx * 100}ms`, animationFillMode: 'both' }}
            >
              <div className={`w-14 h-14 rounded-xl ${r.color} flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300`}>
                <r.icon className="h-7 w-7" />
              </div>
              <h3 className="text-xl font-bold mb-3">{r.title}</h3>
              <p className="text-gray-300 leading-relaxed text-sm">{r.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

// ============================================
// SEZIONE 9C: Mercato Immobiliare 2026
// ============================================
function MercatoSection() {
  const [ref, inView] = useInView<HTMLDivElement>()
  const stats = [
    { value: '+5,2%', label: 'Crescita prezzi 2026', color: 'text-emerald-600' },
    { value: '88', label: 'Giorni medi vendita', color: 'text-blue-600' },
    { value: '€845', label: 'Prezzo medio €/mq', color: 'text-violet-600' },
    { value: '+12%', label: 'Domanda affitti', color: 'text-amber-600' },
  ]
  return (
    <section className="py-24 bg-white relative overflow-hidden">
      <div ref={ref} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className={inView ? 'animate-fade-in-right' : 'opacity-0'}>
            <span className="inline-block text-primary-600 font-semibold text-sm uppercase tracking-wider bg-primary-50 px-4 py-1.5 rounded-full">
              Mercato 2026
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mt-5 leading-tight">
              Il Mercato dell'Hinterland Sud è
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-emerald-500 to-teal-600">
                in Forte Crescita
              </span>
            </h2>
            <p className="mt-6 text-gray-600 text-lg leading-relaxed">
              I 9 comuni che operiamo stanno vivendo un momento di forte espansione.
              La <strong>vicinanza a Cagliari</strong>, l'<strong>aeroporto di Elmas</strong>,
              i miglioramenti infrastrutturali (SS 130, SS 131) e la qualità della vita
              attraggono sempre più famiglie e investitori.
            </p>
            <ul className="mt-8 space-y-4">
              {[
                'Sestu ed Elmas: + 7% sui prezzi nell\'ultimo anno',
                'Assemini: boom della domanda residenziale',
                'Campidano (Villasor, Decimomannu): prezzi accessibili in salita',
                'Investimenti infrastrutturali in corso',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <CheckCircle2 className="h-6 w-6 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
            <div className="mt-10">
              <Link to="/valutazione">
                <button className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold px-8 py-4 text-base rounded-xl shadow-lg hover:shadow-xl transition-all inline-flex items-center gap-2">
                  Sfrutta il Momento — Valuta Ora
                  <ChevronRight className="h-5 w-5" />
                </button>
              </Link>
            </div>
          </div>

          <div className={inView ? 'animate-fade-in-left' : 'opacity-0'}>
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-primary-400 to-emerald-400 rounded-3xl blur-2xl opacity-20" />
              <img
                src={MARKET_IMAGE}
                alt="Mercato immobiliare Cagliari hinterland"
                className="relative w-full rounded-2xl shadow-2xl"
                loading="lazy"
              />
            </div>
            <div className="grid grid-cols-2 gap-4 mt-6">
              {stats.map((s, idx) => (
                <div
                  key={s.label}
                  className={`bg-white border border-gray-200 rounded-2xl p-5 text-center hover:shadow-lg transition-shadow ${
                    inView ? 'animate-count-up' : 'opacity-0'
                  }`}
                  style={{ animationDelay: `${500 + idx * 100}ms`, animationFillMode: 'both' }}
                >
                  <div className={`text-3xl font-bold ${s.color}`}>{s.value}</div>
                  <div className="text-xs text-gray-500 mt-1.5">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export function Home() {
  const [openFaq, setOpenFaq] = useState<number | null>(null)
  const [showAllReviews, setShowAllReviews] = useState(false)
  const [openSeoSection, setOpenSeoSection] = useState<number | null>(null)

  const visibleTestimonials = showAllReviews ? testimonials : testimonials.slice(0, 3)

  return (
    <div className="flex-1 overflow-hidden">
      {/* ===== SEZIONE 1: HERO (Conversion-focused) ===== */}
      <section className="relative text-white min-h-[90vh] flex items-center">
        {/* Background image */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        />
        {/* Dark overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary-900/90 via-primary-800/85 to-primary-900/90" />
        {/* Decorative elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-primary-500/20 rounded-full blur-3xl animate-float" />
          <div className="absolute -bottom-20 -left-20 w-72 h-72 bg-primary-400/15 rounded-full blur-3xl animate-float" style={{ animationDelay: '3s' }} />
          <div className="absolute top-1/2 right-1/4 w-48 h-48 bg-cyan-400/10 rounded-full blur-2xl animate-float" style={{ animationDelay: '1.5s' }} />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 w-full text-center">
          {/* Badge */}
          <div className="animate-on-load animate-fade-in-up inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-5 py-2 mb-8">
            <Shield className="h-4 w-4 text-accent-400" />
            <span className="text-sm font-medium text-primary-100">100% Gratuito &middot; Senza Impegno</span>
          </div>

          <h1 className="animate-on-load animate-fade-in-up text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight tracking-tight">
            Ricevi la Valutazione della Tua
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-accent-300 to-accent-400 mt-2">
              Casa in 2 Minuti
            </span>
          </h1>

          <p className="animate-on-load animate-fade-in-up-delay-1 mt-6 text-lg md:text-xl text-primary-100/90 max-w-2xl mx-auto leading-relaxed">
            Scopri <strong>gratuitamente</strong> quanto vale il tuo immobile nella zona metropolitana di Cagliari.
            Basata sui dati reali delle compravendite degli ultimi 12 mesi.
          </p>

          <div className="animate-on-load animate-fade-in-up-delay-2 mt-10">
            <Link to="/valutazione">
              <button className="pulse-cta bg-accent-400 hover:bg-accent-500 text-gray-900 font-bold px-12 py-5 text-xl md:text-2xl rounded-2xl shadow-xl hover:shadow-2xl transition-all shimmer-btn inline-flex items-center gap-2">
                SCOPRI IL VALORE ORA
                <ChevronRight className="h-6 w-6" />
              </button>
            </Link>
          </div>

          {/* Stats */}
          <div className="animate-on-load animate-fade-in-up-delay-3 mt-12 flex gap-8 justify-center">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-white">{stat.value}</div>
                <div className="text-xs md:text-sm text-primary-200 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SEZIONE 2: TRUST BAR ===== */}
      <section className="bg-gray-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-wrap justify-center gap-6 md:gap-10">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <div className="flex -space-x-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 text-amber-400 fill-amber-400" />
                ))}
              </div>
              <span className="font-semibold">5.0 Google Reviews</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <TrendingUp className="h-4 w-4 text-primary-500" />
              <span>500+ Valutazioni</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Shield className="h-4 w-4 text-primary-500" />
              <span>Dati Compravenduto Reale</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Clock className="h-4 w-4 text-primary-500" />
              <span>Risultato in 2 Min</span>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SEZIONE 3: URGENZA MERCATO ===== */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-full px-4 py-1.5 mb-6">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                <span className="text-sm font-medium text-amber-700">Mercato in evoluzione</span>
              </div>

              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
                Cosa Sta Succedendo ai Prezzi delle Case a {CITY_NAME}?
              </h2>

              <div className="text-gray-600 text-lg leading-relaxed space-y-5">
                <p>
                  Il mercato immobiliare a {CITY_NAME} sta cambiando rapidamente. I prezzi variano di zona in zona e mese dopo mese. <strong>Sapere quanto vale realmente la tua casa oggi</strong> pu&ograve; fare la differenza di migliaia di euro.
                </p>
                <p>
                  Non affidarti a stime generiche o a portali che usano medie nazionali. La nostra valutazione si basa sulle <strong>compravendite effettive degli ultimi 12 mesi</strong> nella tua zona specifica di {CITY_NAME}.
                </p>
              </div>

              <div className="mt-10">
                <Link to="/valutazione">
                  <Button size="lg" className="px-8 py-3 text-base font-semibold shadow-lg hover:shadow-xl transition-all rounded-xl">
                    Scopri il Valore Reale
                    <ChevronRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </div>

            <div className="hidden lg:block">
              <img
                src={MARKET_IMAGE}
                alt="Mercato immobiliare Cagliari - valutazione case e compravendite hinterland cagliaritano"
                loading="lazy"
                className="w-full rounded-2xl shadow-xl object-cover aspect-[4/5]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ===== SEZIONE 4: COME FUNZIONA ===== */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-primary-600 font-semibold text-sm uppercase tracking-wider">Semplice e veloce</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3">
              Come Funziona la Valutazione Immobiliare a {CITY_NAME}
            </h2>
            <p className="mt-4 text-gray-500 max-w-2xl mx-auto">
              Tre semplici passaggi per scoprire il valore della tua casa a {CITY_NAME} basato su dati reali del compravenduto.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
            {/* Connecting line on desktop */}
            <div className="hidden md:block absolute top-16 left-[20%] right-[20%] h-0.5 bg-gradient-to-r from-primary-200 via-primary-400 to-primary-200" />

            {steps.map((step, index) => (
              <div key={step.number} className="relative text-center" style={{ animationDelay: `${index * 0.15}s` }}>
                <div className="relative inline-flex items-center justify-center w-32 h-32 mb-6">
                  <div className="absolute inset-0 rounded-full bg-primary-50" />
                  <div className="relative w-16 h-16 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center shadow-lg">
                    <step.icon className="h-8 w-8 text-white" />
                  </div>
                  <div className="absolute -top-1 -right-1 w-8 h-8 rounded-full bg-primary-600 text-white text-sm font-bold flex items-center justify-center shadow">
                    {step.number}
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed max-w-xs mx-auto">{step.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/valutazione">
              <button className="bg-accent-400 hover:bg-accent-500 text-gray-900 font-bold px-10 py-4 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all inline-flex items-center gap-2">
                INIZIAMO
                <ChevronRight className="h-5 w-5" />
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* ===== SEZIONE 5: TESTIMONIANZE ===== */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-primary-600 font-semibold text-sm uppercase tracking-wider">Recensioni</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3">
              Cosa Dicono i Nostri Clienti
            </h2>
            {/* Google aggregate badge */}
            <div className="mt-4 inline-flex items-center gap-2 bg-gray-50 rounded-full px-5 py-2">
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              <div className="flex -space-x-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 text-amber-400 fill-amber-400" />
                ))}
              </div>
              <span className="text-sm font-semibold text-gray-700">5.0 su Google</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {visibleTestimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-2xl p-6 border border-gray-100"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 text-amber-400 fill-amber-400" />
                    ))}
                  </div>
                  <svg className="h-5 w-5 text-gray-400" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed mb-6">
                  "{testimonial.text}"
                </p>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full ${testimonial.bgColor} flex items-center justify-center`}>
                    <span className="text-white font-semibold text-sm">
                      {testimonial.initials}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{testimonial.name}</p>
                    <p className="text-xs text-gray-400">Google Review &middot; {testimonial.date}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {!showAllReviews && (
            <div className="text-center mt-8">
              <button
                onClick={() => setShowAllReviews(true)}
                className="text-primary-600 hover:text-primary-700 font-semibold text-sm inline-flex items-center gap-1 transition-colors"
              >
                Vedi tutte le recensioni
                <ChevronDown className="h-4 w-4" />
              </button>
            </div>
          )}

          <div className="text-center mt-8">
            <Link to="/valutazione" className="text-primary-600 hover:text-primary-700 font-semibold text-sm inline-flex items-center gap-1 transition-colors">
              Anche tu puoi scoprire il valore della tua casa
              <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== SEZIONE 6: CHI SIAMO ===== */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-primary-600 font-semibold text-sm uppercase tracking-wider">Consulenti immobiliari</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3">
              Chi Siamo?
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src={AGENCY_IMAGE}
                alt="Consulenti immobiliari - valutazioni nella zona metropolitana di Cagliari"
                loading="lazy"
                className="w-full rounded-2xl shadow-lg mb-8 object-cover aspect-[4/3]"
              />
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                Siamo <strong>consulenti immobiliari</strong> attivi nella zona metropolitana di Cagliari, nel cuore del Sud Sardegna.
                Con <strong>oltre 15 anni di esperienza</strong> nel mercato immobiliare locale, conosciamo
                ogni angolo del territorio e ogni sfumatura del mercato.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed mb-8">
                La nostra missione &egrave; offrire <strong>valutazioni trasparenti e accurate</strong>, basate su dati reali
                del compravenduto, per aiutarti a prendere le migliori decisioni immobiliari. Centinaia di clienti
                si sono gi&agrave; affidati a noi con successo.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center">
                    <Award className="h-5 w-5 text-primary-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">15+ Anni</p>
                    <p className="text-xs text-gray-500">di esperienza</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center">
                    <Users className="h-5 w-5 text-primary-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">500+</p>
                    <p className="text-xs text-gray-500">clienti serviti</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center">
                    <MapPin className="h-5 w-5 text-primary-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Area metropolitana</p>
                    <p className="text-xs text-gray-500">esperti locali</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact card */}
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-primary-50 flex items-center justify-center">
                  <Building2 className="h-6 w-6 text-primary-600" />
                </div>
                <div>
                  <h3 className="font-bold text-xl text-gray-900">Valutazione Immobili Assemini</h3>
                  <p className="text-sm text-gray-500">Consulenti immobiliari</p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-primary-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-600">
                    Via Sicilia, 93<br />
                    Assemini (CA) - 09032
                  </p>
                </div>

                <a href="tel:+393318601890" className="flex items-center gap-3 text-sm text-gray-600 hover:text-primary-600 transition-colors group">
                  <Phone className="h-5 w-5 text-primary-500" />
                  +39 331 860 1890
                </a>

                <a href="mailto:laassemini@gmail.com" className="flex items-center gap-3 text-sm text-gray-600 hover:text-primary-600 transition-colors group">
                  <Mail className="h-5 w-5 text-primary-500" />
                  laassemini@gmail.com
                </a>

                <a href="https://valutazioniassemini.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-sm text-gray-600 hover:text-primary-600 transition-colors group">
                  <Globe className="h-5 w-5 text-primary-500" />
                  valutazioniassemini.com
                </a>
              </div>

              {/* Trust badge */}
              <div className="mt-6 pt-4 border-t border-gray-100 flex items-center gap-2">
                <div className="flex -space-x-0.5">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 text-amber-400 fill-amber-400" />
                  ))}
                </div>
                <span className="text-xs text-gray-500 ml-1">5.0 su Google &middot; Recensioni verificate</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SEZIONE 7: VANTAGGI ===== */}
      <section className="py-20 bg-white relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-primary-600 font-semibold text-sm uppercase tracking-wider">I nostri vantaggi</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3">
              Perch&eacute; Scegliere la Nostra Valutazione Immobiliare
            </h2>
            <p className="mt-4 text-gray-500 max-w-2xl mx-auto">
              Tecnologia avanzata e dati reali del compravenduto per una valutazione precisa e affidabile del tuo immobile a {CITY_NAME}.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div
                key={feature.title}
                className="feature-card bg-gray-50 rounded-2xl p-6 border border-gray-100"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-5 shadow-lg`}>
                  <feature.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SEZIONE SEO: MERCATO IMMOBILIARE 2026 ===== */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">
            Mercato Immobiliare {CITY_NAME} 2026: Prezzi e Previsioni
          </h2>
          <div className="prose prose-lg max-w-none text-gray-600 space-y-5 leading-relaxed">
            <p>
              Il <strong>mercato immobiliare a {CITY_NAME} nel 2026</strong> si conferma stabile con segnali di crescita, sostenuto dalla domanda costante di residenti, studenti universitari e professionisti. Dopo un periodo di consolidamento, i <strong>prezzi delle case a {CITY_NAME}</strong> mostrano un trend positivo soprattutto nelle zone centrali e ben servite, dove la disponibilità di immobili di qualità è limitata. Le zone residenziali periferiche registrano un interesse crescente, in particolare da parte di famiglie che cercano spazi più ampi e prezzi accessibili.
            </p>
            <p>
              Tra i fattori che influenzano il <strong>mercato immobiliare dell'hinterland cagliaritano nel 2026</strong>, spiccano la vicinanza al capoluogo, all'aeroporto di Elmas, i miglioramenti infrastrutturali delle SS 130 e SS 131 e la crescente domanda di <strong>residenza fuori dal centro</strong> con maggiori spazi e prezzi accessibili. La vicinanza alle spiagge del <strong>Poetto</strong> e del <strong>Sud Sardegna</strong> aggiunge attrattività all'area come base residenziale.
            </p>
            <p>
              Per chi sta valutando di <strong>comprare o vendere casa a {CITY_NAME}</strong> nel 2026, è fondamentale avere una <strong>valutazione aggiornata</strong> basata sulle transazioni più recenti. Il nostro strumento utilizza i dati del compravenduto degli ultimi 12 mesi, garantendo una stima che riflette le condizioni attuali del mercato e non dati storici ormai superati. Che tu stia pensando di vendere il tuo appartamento, acquistare un immobile o semplicemente monitorare il valore del tuo patrimonio immobiliare, una valutazione periodica è il modo migliore per restare informato.
            </p>
          </div>
        </div>
      </section>

      {/* ===== SEZIONE 8: CONTENUTI SEO (Accordion) ===== */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-primary-600 font-semibold text-sm uppercase tracking-wider">Approfondimenti</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3">
              Tutto sul Mercato Immobiliare nella Zona Metropolitana di Cagliari
            </h2>
          </div>

          <div className="space-y-4">
            {seoSections.map((section, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenSeoSection(openSeoSection === index ? null : index)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="text-lg font-semibold text-gray-900 pr-4">{section.title}</span>
                  <ChevronDown
                    className={`h-5 w-5 text-gray-400 flex-shrink-0 transition-transform duration-200 ${
                      openSeoSection === index ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                <div className={openSeoSection === index ? 'block' : 'sr-only'}>
                  <div className="px-5 pb-5 prose prose-lg max-w-none text-gray-600 leading-relaxed">
                    {section.content}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SEZIONE 9: I 9 COMUNI DI ECCELLENZA ===== */}
      <ComuniSection />

      {/* ===== SEZIONE 9B: PERCHÉ SCEGLIERE NOI ===== */}
      <PerchéSection />

      {/* ===== SEZIONE 9C: MERCATO 2026 ===== */}
      <MercatoSection />

      {/* ===== SEZIONE 10: FAQ ===== */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-primary-600 font-semibold text-sm uppercase tracking-wider">FAQ</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3">
              Domande Frequenti sulla Valutazione Casa a {CITY_NAME}
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white border border-gray-200 rounded-xl overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="text-base font-semibold text-gray-900 pr-4">{faq.question}</span>
                  <ChevronDown
                    className={`h-5 w-5 text-gray-400 flex-shrink-0 transition-transform duration-200 ${
                      openFaq === index ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {openFaq === index && (
                  <div className="px-5 pb-5">
                    <p className="text-gray-600 text-sm leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SEZIONE SEO LOCALE: ZONE, QUARTIERI E LONG-TAIL ===== */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-primary-600 font-semibold text-sm uppercase tracking-wider">Guida completa</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3">
              Valutazione Immobiliare a {CITY_NAME}: Quartieri, Prezzi e Trend 2026
            </h2>
            <p className="mt-4 text-gray-500 max-w-3xl mx-auto">
              Una guida aggiornata su come valutare correttamente la tua casa in ogni zona dell'hinterland cagliaritano.
              Prezzi al metro quadro, fattori che influenzano il valore e consigli per vendere meglio.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <article className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Quanto vale una casa a Cagliari al metro quadro?</h3>
              <p className="text-gray-600 leading-relaxed">
                I <strong>prezzi al metro quadro a Cagliari</strong> nel 2026 oscillano fra 1.500 e 3.500 €/mq nelle zone centrali (Castello, Marina, Villanova, Stampace) e fra 1.000 e 2.000 €/mq nei quartieri residenziali (Genneruxi, Quartiere Europeo, San Benedetto). Nell'<strong>hinterland di Cagliari</strong> i valori partono da 600-800 €/mq (Villasor, Decimoputzu, Villaspeciosa) e salgono fino a 1.200-1.500 €/mq nei comuni più richiesti come Sestu, Elmas e Quartu Sant'Elena. La nostra <strong>valutazione casa Cagliari online gratuita</strong> calcola il prezzo specifico in base alla zona, alle caratteristiche dell'immobile e al compravenduto reale.
              </p>
            </article>

            <article className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Vendere casa nell'hinterland di Cagliari nel 2026</h3>
              <p className="text-gray-600 leading-relaxed">
                Per <strong>vendere casa a Cagliari e nell'hinterland</strong> al miglior prezzo è fondamentale partire da una <strong>valutazione immobiliare aggiornata</strong> e da una corretta strategia di marketing. I tempi medi di vendita variano da 4 a 8 mesi a seconda della zona e della tipologia. Comuni come <strong>Sestu</strong> e <strong>Elmas</strong>, grazie alla vicinanza al capoluogo e all'aeroporto, registrano tempi più rapidi. Una <strong>stima immobile online</strong> precisa ti evita di sopravvalutare il prezzo (rischio invenduto) o sottovalutarlo (perdita economica).
              </p>
            </article>

            <article className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Comprare casa a Cagliari: come scegliere la zona giusta</h3>
              <p className="text-gray-600 leading-relaxed">
                Stai pensando di <strong>comprare casa a Cagliari</strong>? Le opzioni vanno dal centro storico (palazzi d'epoca, ottimo investimento) ai quartieri residenziali moderni (Poetto, San Benedetto) fino all'hinterland (più spazio, prezzi accessibili, ottimi collegamenti). <strong>Assemini</strong>, <strong>Sestu</strong>, <strong>Elmas</strong>, <strong>Decimomannu</strong>, <strong>San Sperate</strong>, <strong>Uta</strong>, <strong>Villasor</strong>, <strong>Decimoputzu</strong> e <strong>Villaspeciosa</strong> offrono case indipendenti, ville e appartamenti a prezzi 30-50% inferiori al capoluogo, con ottimi collegamenti SS 130, SS 131 e ferroviari.
              </p>
            </article>

            <article className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Cosa influenza il valore di una casa a Cagliari?</h3>
              <p className="text-gray-600 leading-relaxed">
                Il <strong>valore di un immobile a Cagliari</strong> dipende da diversi fattori: <strong>zona</strong> (centro vs periferia vs hinterland), <strong>stato di conservazione</strong> (ristrutturato +15-20%, da ristrutturare -20-30%), <strong>piano</strong> (con o senza ascensore), presenza di <strong>balcone, terrazzo, giardino</strong>, <strong>posto auto</strong> o <strong>garage</strong>, <strong>classe energetica</strong>, vista mare/Castello e prossimità a servizi (scuole, ospedale Brotzu, università, Poetto, aeroporto Elmas). La nostra <strong>perizia online gratuita</strong> applica coefficienti specifici per ognuno di questi parametri.
              </p>
            </article>
          </div>

          <div className="bg-gradient-to-br from-primary-50 to-white rounded-2xl p-8 border border-primary-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Perché scegliere la nostra valutazione casa Cagliari online
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-600 leading-relaxed">
              <p>
                <strong>Dati reali del compravenduto:</strong> non medie nazionali, ma transazioni effettive registrate negli ultimi 12 mesi in ogni microzona di Cagliari e dei 9 comuni dell'hinterland.
              </p>
              <p>
                <strong>Algoritmo trasparente:</strong> il calcolo applica coefficienti pubblici per zona, tipologia, stato, piano e dotazioni — niente "black box", la stima è verificabile.
              </p>
              <p>
                <strong>100% gratuito e senza impegno:</strong> nessun obbligo di vendita, nessun costo nascosto. Ricevi la <strong>valutazione immobile Cagliari</strong> via email in 2 minuti.
              </p>
              <p>
                <strong>Consulenza locale opzionale:</strong> se vuoi vendere, il nostro team con oltre 15 anni di esperienza nel mercato immobiliare cagliaritano è a disposizione per una consulenza personalizzata gratuita.
              </p>
            </div>
            <div className="mt-8 text-center">
              <Link to="/valutazione">
                <Button size="lg" className="px-8 py-3 text-base font-semibold shadow-lg hover:shadow-xl transition-all rounded-xl">
                  Valuta Subito la Tua Casa
                  <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>

          <div className="mt-12 text-center text-sm text-gray-500">
            <p>
              Parole chiave: <em>valutazione casa Cagliari, valutazione immobile Cagliari online gratis, quanto vale la mia casa Cagliari, stima casa Cagliari hinterland, prezzo al mq Cagliari, valutazione casa Assemini, Sestu, Elmas, Decimomannu, San Sperate, Uta, Villasor, Decimoputzu, Villaspeciosa, vendere casa Cagliari, comprare casa Cagliari, agenzia immobiliare Cagliari, perizia online gratuita Cagliari, mercato immobiliare Sardegna 2026.</em>
            </p>
          </div>
        </div>
      </section>

      {/* ===== SEZIONE 11: CTA FINALE ===== */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-600 to-primary-800" />
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-20 -right-20 w-72 h-72 bg-white/5 rounded-full blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <p className="text-primary-200 text-sm font-medium mb-4 uppercase tracking-wider">
            Non aspettare che i prezzi cambino
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Scopri Subito Quanto Vale la Tua Casa a {CITY_NAME}
          </h2>
          <p className="text-xl text-primary-100 mb-10 max-w-2xl mx-auto">
            Compila il form in 2 minuti e ricevi la valutazione immobiliare gratuita
            basata sui dati reali del compravenduto degli ultimi 12 mesi.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/valutazione">
              <button className="pulse-cta bg-accent-400 hover:bg-accent-500 text-gray-900 font-bold px-12 py-5 text-xl rounded-2xl shadow-xl hover:shadow-2xl transition-all shimmer-btn inline-flex items-center gap-2">
                SCOPRI IL VALORE ORA
                <ChevronRight className="h-6 w-6" />
              </button>
            </Link>
          </div>
          <p className="mt-6 text-primary-200 text-sm">
            Oppure chiama il{' '}
            <a href="tel:+393318601890" className="text-white font-semibold hover:underline">
              +39 331 860 1890
            </a>
            {' '}per una consulenza personalizzata
          </p>

          <div className="mt-8 flex flex-wrap justify-center gap-6 text-primary-200 text-xs">
            <span className="flex items-center gap-1.5">
              <CheckCircle2 className="h-4 w-4" />
              100% Gratuito
            </span>
            <span className="flex items-center gap-1.5">
              <CheckCircle2 className="h-4 w-4" />
              Dati Reali Compravenduto
            </span>
            <span className="flex items-center gap-1.5">
              <CheckCircle2 className="h-4 w-4" />
              Risultato in 2 Minuti
            </span>
          </div>
        </div>
      </section>
    </div>
  )
}
